import React, { useState, useEffect } from "react";
import { useNavigate, useParams, Link } from 'react-router-dom';
import 'react-toastify/dist/ReactToastify.css';
import toast, { Toaster } from 'react-hot-toast';
import AuthUtility from "../../util/AuthUtility";
import { Col, Button, Row, Container, Card, Form, Table, Tab, Tabs, Badge, Carousel, Pagination } from 'react-bootstrap';
import {
    getAttributeStatus,
    getAuctionPermissionBadge,
    getAuctionStatusBadge,
    getAuctionTypeBadge,
    URL_BACKEND_ROOT
} from "../../app/AppConst";
import { onBidSearch, onListingDetail, onOfferSearch } from "../../controller/ListingController";
import { ListingDetailEntity } from "../../dto/listing/ListingDetailEntity";
import { WalletTxnEntity } from "../../dto/wallet/WalletTxnEntity";
import { WalletTxnSearch } from "../../dto/wallet/WalletTxnSearch";
import { PageableBean } from "../../dto/pageable/PageableBean";
import { onWalletTxnSearch } from "../../controller/WalletController";
import { BidSearch } from "../../dto/bid/BidSearch";
import { BidEntity } from "../../dto/bid/BidEntity";
import { ListingSuspendModal } from "../../component/ListingSuspendModal";
import { ListingCancelModal } from "../../component/ListingCancelModal";
import { ListingRestoreModal } from "../../component/ListingRestoreModal";
import { ListingPayoutModal } from "../../component/ListingPayoutModal";
import { OfferSearch } from "../../dto/offer/OfferSearch";
import { OfferEntity } from "../../dto/offer/OfferEntity";

type ListingParams = {
    listingId: string;
};

const ListingDetail = () => {
    const { listingId } = useParams<ListingParams>();
    const [activeKey, setActiveKey] = useState<string>('listing-info');
    const [activeIndex, setActiveIndex] = useState(0);
    const [listingDetail, setListingDetail] = useState<ListingDetailEntity>({});

    // modal visibility
    const [showSuspendModal, setShowSuspendModal] = useState(false);
    const [showCancelModal,  setShowCancelModal]  = useState(false);
    const [showRestoreModal, setShowRestoreModal] = useState(false);
    const [showPayoutModal,  setShowPayoutModal]  = useState(false);

    const navigate = useNavigate();

    useEffect(() => {
        if (!AuthUtility.isUserLoggedIn()) {
            navigate("/login");
            window.location.reload();
        } else {
            listingDetailGet();
        }
    }, []);

    const listingDetailGet = async () => {
        const response = await onListingDetail(parseInt(listingId!));
        const data = response.data;
        if (data !== undefined) {
            setListingDetail({
                listing:      data.listing,
                account:      data.account,
                product:      data.product,
                medias:       data.medias,
                attributes:   data.attributes,
                wallet:       data.wallet,
                transactions: data.transactions,
                bids:         data.bids
            });
        }
    };

    // ── wallet txn pagination ──
    const [walletTxnSearch, setWalletTxnSearch] = useState<WalletTxnSearch>({ limit: 10 });

    const changePage = (page: number) => {
        if (!listingDetail.transactions) return;
        if (page < 0 || page >= listingDetail.transactions.totalPages!) return;
        load(page);
    };

    const load = async (page: number = 0) => {
        const params: WalletTxnSearch = {
            ...walletTxnSearch,
            walletId: listingDetail.wallet?.id,
            offset: page
        };
        const response = await onWalletTxnSearch(params);
        if (response.status !== 0) { toast.error("Wallet transaction read error."); return; }
        const searchParam: PageableBean<WalletTxnEntity> = {
            totalPages:    response.data.totalPages,
            totalElements: response.data.totalElements,
            offset:        page,
            beans:         response.data.beans
        };
        setListingDetail(prev => ({ ...prev, transactions: searchParam }));
        setWalletTxnSearch(prev => ({ ...prev, offset: page }));
    };

    // ── bid pagination ──
    const [bidSearch, setBidSearch] = useState<BidSearch>({ limit: 10 });

    const changeBidPage = (page: number) => {
        if (!listingDetail.bids) return;
        if (page < 0 || page >= listingDetail.bids.totalPages!) return;
        loadBids(page);
    };

    const loadBids = async (page: number = 0) => {
        const params: BidSearch = {
            ...bidSearch,
            listingId: listingDetail.listing?.id,
            offset: page
        };
        const response = await onBidSearch(params);
        if (response.status !== 0) { toast.error("Bid search error."); return; }
        const searchParam: PageableBean<BidEntity> = {
            totalPages:    response.data.totalPages,
            totalElements: response.data.totalElements,
            offset:        page,
            beans:         response.data.beans
        };
        setListingDetail(prev => ({ ...prev, bids: searchParam }));
        setBidSearch(prev => ({ ...prev, offset: page }));
    };

    const getStatusVariant = (status?: string) => {
        switch (status) {
            case "APPROVED": case "ACCEPTED": return "success";
            case "REJECTED":                  return "danger";
            case "PENDING":  case "SUBMITTED": return "warning";
            case "EXPIRED":                   return "info";
            default:                          return "secondary";
        }
    };

    // ── which admin actions are available depends solely on current status ──
    // ACTIVE    → Suspend
    // SUSPENDED → Restore  |  Cancel
    // ENDED     → Manual Payout
    const status = listingDetail.listing?.status;


    const [offerSearch, setOfferSearch] = useState<OfferSearch>({ limit: 10 });

    const changeOfferPage = (page: number) => {
        if (!listingDetail.offers) return;
        if (page < 0 || page >= listingDetail.offers.totalPages!) return;
        loadBids(page);
    };

    const loadOffers = async (page: number = 0) => {
        const params: OfferSearch = {
            ...offerSearch,
            listingId: listingDetail.listing?.id,
            offset: page
        };
        const response = await onOfferSearch(params);
        if (response.status !== 0) { toast.error("Offer search error."); return; }
        const searchParam: PageableBean<OfferEntity> = {
            totalPages:    response.data.totalPages,
            totalElements: response.data.totalElements,
            offset:        page,
            beans:         response.data.beans
        };
        setListingDetail(prev => ({ ...prev, offers: searchParam }));
        setOfferSearch(prev => ({ ...prev, offset: page }));
    };

    return (
        <div className="pl-30 pr-30">
            <div><Toaster position="top-right" reverseOrder={false} /></div>

            <Container fluid>
                <div className="d-flex justify-content-between align-items-center mb-3">
                    <h3 className="mb-0">
                        <a onClick={() => navigate(-1)} style={{ cursor: "pointer" }}>
                            <big>&#8592;</big>
                        </a>
                        Auction Detail
                    </h3>

                    {/* ── Admin action buttons — render only what is valid for current status ── */}
                    <div className="d-flex gap-2">
                        {status === "ACTIVE" && (
                            <Button variant="outline-warning" size="sm"
                                onClick={() => setShowSuspendModal(true)}>
                                ⏸️ Suspend
                            </Button>
                        )}
                        {status === "SUSPENDED" && (<>
                            <Button variant="outline-success" size="sm"
                                onClick={() => setShowRestoreModal(true)}>
                                ▶️ Restore
                            </Button>
                            <Button variant="outline-danger" size="sm"
                                onClick={() => setShowCancelModal(true)}>
                                🚫 Cancel
                            </Button>
                        </>)}
                        {/* {status === "ENDED" && (
                            <Button variant="outline-success" size="sm"
                                onClick={() => setShowPayoutModal(true)}>
                                💸 Manual Payout
                            </Button>
                        )} */}
                    </div>
                </div>
            </Container>

            <Container fluid>
                <Row className="d-flex">
                    {/* ── Seller profile ── */}
                    <Col md={3} lg={3} xs={3}>
                        <div className="mb-30">
                            <Card>
                                <Card.Header>Seller Profile</Card.Header>
                                <Card.Body>
                                    <div className="thumbnail-wrapper">
                                        <a href={`${URL_BACKEND_ROOT}/v1/file/api/files/${listingDetail.account?.avatarFileUuid}`}
                                            target="_blank"
                                            style={{ width: "100%", height: "100%", textAlign: "center" }}>
                                            <img
                                                src={`${URL_BACKEND_ROOT}/v1/file/d/${listingDetail.account?.avatarFileUuid}`}
                                                alt="profile-icon"
                                                style={{ maxWidth: "100%", maxHeight: "100%" }}
                                            />
                                        </a>
                                    </div>
                                </Card.Body>
                                <Card.Footer>
                                    <Table hover>
                                        <tbody>
                                            <tr>
                                                <th>Lastname</th>
                                                <td>{listingDetail.account?.individualProfile?.lastName}</td>
                                            </tr>
                                            <tr>
                                                <th>Firstname</th>
                                                <td>{listingDetail.account?.individualProfile?.firstName}</td>
                                            </tr>
                                            <tr>
                                                <th>Register No</th>
                                                <td>{listingDetail.account?.individualProfile?.regNo}</td>
                                            </tr>
                                            <tr>
                                                <th>Phone No</th>
                                                <td>
                                                    <Link
                                                        to={`/user/${listingDetail.account?.phoneNo}`}
                                                        className="text-primary text-decoration-underline">
                                                        {listingDetail.account?.phoneNo}
                                                    </Link>
                                                </td>
                                            </tr>
                                            <tr>
                                                <th>KYC Level</th>
                                                <td>{listingDetail.account?.kycLevel}</td>
                                            </tr>
                                            <tr>
                                                <th>KYC Status</th>
                                                <td>
                                                    <Badge bg={getStatusVariant(listingDetail.account?.kycStatus)}>
                                                        {listingDetail.account?.kycStatus}
                                                    </Badge>
                                                </td>
                                            </tr>
                                        </tbody>
                                    </Table>
                                </Card.Footer>
                            </Card>
                        </div>
                    </Col>

                    {/* ── Tabs ── */}
                    <Col md={9} lg={9} xs={9}>
                        <Tabs
                            defaultActiveKey="listing-info"
                            id="listing-info-tab"
                            className="mb-3"
                            activeKey={activeKey}
                            onSelect={(k) => k && setActiveKey(k)}
                        >
                            {/* Basic Info */}
                            <Tab eventKey="listing-info" title="Basic Info">
                                <Table hover>
                                    <thead>
                                        <tr>
                                            <th>Created At</th>
                                            <th>Type</th>
                                            <th>Auction Type</th>
                                            <th>Permission</th>
                                            <th>Status</th>
                                            <th>Currency</th>
                                            <th>Start Price</th>
                                            <th>Buy Now Price</th>
                                            <th>Starts At</th>
                                            <th>Ends At</th>
                                        </tr>
                                    </thead>
                                    <tbody>
                                        <tr>
                                            <td>{listingDetail.listing?.createdAt ? new Date(listingDetail.listing.createdAt).toLocaleString() : ''}</td>
                                            <td><Badge bg={getAuctionTypeBadge(listingDetail.listing?.type)}>{listingDetail.listing?.type}</Badge></td>
                                            <td>{listingDetail.listing?.auctionType}</td>
                                            <td><Badge bg={getAuctionPermissionBadge(listingDetail.listing?.permission)}>{listingDetail.listing?.permission}</Badge></td>
                                            <td><Badge bg={getAuctionStatusBadge(listingDetail.listing?.status)}>{listingDetail.listing?.status}</Badge></td>
                                            <td>{listingDetail.listing?.currency}</td>
                                            <td>{listingDetail.listing?.startPrice}</td>
                                            <td>{listingDetail.listing?.buyNowPrice}</td>
                                            <td>{listingDetail.listing?.startsAt ? new Date(listingDetail.listing.startsAt).toLocaleString() : ''}</td>
                                            <td>{listingDetail.listing?.endsAt ? new Date(listingDetail.listing.endsAt).toLocaleString() : ''}</td>
                                        </tr>
                                    </tbody>
                                </Table>
                            </Tab>

                            {/* Wallet */}
                            <Tab eventKey="listing-wallet" title="Wallet">
                                <Row className="mt-3">
                                    <Col md={4}>
                                        <Card className="mb-3 shadow-sm">
                                            <Card.Body>
                                                <h6 className="text-muted">Total Balance</h6>
                                                <h2 style={{ fontWeight: 700 }}>
                                                    {listingDetail.wallet?.currency || "MNT"}{" "}
                                                    {listingDetail.wallet?.balance?.toLocaleString() || "0"}
                                                </h2>
                                                <div className="d-flex justify-content-between mt-3">
                                                    <div>
                                                        <small className="text-muted">Available</small>
                                                        <div>
                                                            {listingDetail.wallet?.currency || "MNT"}{" "}
                                                            {listingDetail.wallet?.balance?.toLocaleString() || "0"}
                                                        </div>
                                                    </div>
                                                    <div>
                                                        <small className="text-muted">Hold</small>
                                                        <div className="text-danger">
                                                            {listingDetail.wallet?.currency || "MNT"}{" "}
                                                            {listingDetail.wallet?.hold?.toLocaleString() || "0"}
                                                        </div>
                                                    </div>
                                                </div>
                                            </Card.Body>
                                        </Card>
                                    </Col>
                                    <Col md={8}>
                                        <Card className="shadow-sm">
                                            <Card.Header>Recent Transactions</Card.Header>
                                            <Card.Body className="p-0">
                                                <Table hover responsive className="mb-0">
                                                    <thead>
                                                        <tr>
                                                            <th>Date</th>
                                                            <th>Description</th>
                                                            <th>In/Out</th>
                                                            <th>Type</th>
                                                            <th>Amount</th>
                                                        </tr>
                                                    </thead>
                                                    <tbody>
                                                        {(listingDetail.transactions?.beans ?? []).length > 0 ? (
                                                            listingDetail.transactions!.beans.map((tx, idx) => (
                                                                <tr key={idx}>
                                                                    <td>{tx.createdAt ? new Date(tx.createdAt).toLocaleString() : "-"}</td>
                                                                    <td>{tx.description}</td>
                                                                    <td>{tx.direction}</td>
                                                                    <td>{tx.txnType}</td>
                                                                    <td>{tx.amount}</td>
                                                                </tr>
                                                            ))
                                                        ) : (
                                                            <tr>
                                                                <td colSpan={5} className="text-center text-muted py-3">No records.</td>
                                                            </tr>
                                                        )}
                                                    </tbody>
                                                </Table>
                                            </Card.Body>
                                        </Card>
                                        {listingDetail.transactions && listingDetail.transactions.totalPages > 1 && (() => {
                                            const page = listingDetail.transactions;
                                            const current = page.offset ?? 0;
                                            const start = Math.max(current - 2, 0);
                                            const end = Math.min(start + 5, page.totalPages);
                                            return (
                                                <Row>
                                                    <Col className="d-flex justify-content-start mt-3">
                                                        <Pagination>
                                                            <Pagination.First disabled={current === 0} onClick={() => changePage(0)} />
                                                            <Pagination.Prev  disabled={current === 0} onClick={() => changePage(current - 1)} />
                                                            {Array.from({ length: end - start }).map((_, i) => {
                                                                const p = start + i;
                                                                return <Pagination.Item key={p} active={p === current} onClick={() => changePage(p)}>{p + 1}</Pagination.Item>;
                                                            })}
                                                            <Pagination.Next disabled={current + 1 >= page.totalPages} onClick={() => changePage(current + 1)} />
                                                            <Pagination.Last disabled={current + 1 >= page.totalPages} onClick={() => changePage(page.totalPages - 1)} />
                                                        </Pagination>
                                                    </Col>
                                                </Row>
                                            );
                                        })()}
                                    </Col>
                                </Row>
                            </Tab>

                            {/* Bids */}
                            <Tab eventKey="bid-info" title="Bid">
                                <Card className="shadow-sm">
                                    <Card.Header>
                                        <Row className="mb-2">
                                            <Col><strong>Recent Bids</strong></Col>
                                        </Row>
                                        <Row className="g-2 align-items-center">
                                            <Col md={3}>
                                                <Form.Control size="sm" placeholder="Nickname"
                                                    value={bidSearch.nickname || ""}
                                                    onChange={(e) => {
                                                        const v = e.target.value;
                                                        v ? setBidSearch({ ...bidSearch, nickname: v })
                                                          : (() => { const { nickname, ...rest } = bidSearch; setBidSearch(rest); })();
                                                    }} />
                                            </Col>
                                            <Col md={3}>
                                                <Form.Control size="sm" placeholder="Phone"
                                                    value={bidSearch.phoneNo || ""}
                                                    onChange={(e) => {
                                                        const v = e.target.value;
                                                        v ? setBidSearch({ ...bidSearch, phoneNo: v })
                                                          : (() => { const { phoneNo, ...rest } = bidSearch; setBidSearch(rest); })();
                                                    }} />
                                            </Col>
                                            <Col md={2}>
                                                <Form.Select size="sm" value={bidSearch.limit || 10}
                                                    onChange={(e) => setBidSearch(prev => ({
                                                        ...prev,
                                                        limit: Number(e.target.value) === 0 ? undefined : Number(e.target.value)
                                                    }))}>
                                                    <option value={10}>10</option>
                                                    <option value={25}>25</option>
                                                    <option value={0}>All</option>
                                                </Form.Select>
                                            </Col>
                                            <Col md={2} className="d-grid">
                                                <Button size="sm" onClick={() => loadBids(0)}>🔍 Search</Button>
                                            </Col>
                                        </Row>
                                    </Card.Header>
                                    <Card.Body className="p-0">
                                        <Table hover responsive className="mb-0">
                                            <thead>
                                                <tr>
                                                    <th>Created At</th>
                                                    <th>Account</th>
                                                    <th>Status</th>
                                                    <th>Currency</th>
                                                    <th>Amount</th>
                                                    <th>Source</th>
                                                </tr>
                                            </thead>
                                            <tbody>
                                                {(listingDetail.bids?.beans ?? []).length > 0 ? (
                                                    listingDetail.bids!.beans.map((bid, bidIdx) => (
                                                        <tr key={bidIdx}>
                                                            <td>{bid.createdAt ? new Date(bid.createdAt).toLocaleString() : "-"}</td>
                                                            <td>{bid.nickname}, {bid.phoneNo}</td>
                                                            <td>
                                                                <Badge bg={
                                                                    bid.status === "WINNING" ? "success"
                                                                    : bid.status === "OUTBID" ? "secondary"
                                                                    : "light"
                                                                } text={bid.status === "OUTBID" ? undefined : "dark"}>
                                                                    {bid.status}
                                                                </Badge>
                                                            </td>
                                                            <td>{bid.currency}</td>
                                                            <td>{bid.amount}</td>
                                                            <td>{bid.source}</td>
                                                        </tr>
                                                    ))
                                                ) : (
                                                    <tr>
                                                        <td colSpan={6} className="text-center text-muted py-3">No records.</td>
                                                    </tr>
                                                )}
                                            </tbody>
                                        </Table>
                                    </Card.Body>
                                </Card>
                                {listingDetail.bids && listingDetail.bids.totalPages > 1 && (() => {
                                    const page = listingDetail.bids;
                                    const current = page.offset ?? 0;
                                    const start = Math.max(current - 2, 0);
                                    const end = Math.min(start + 5, page.totalPages);
                                    return (
                                        <Row>
                                            <Col className="d-flex justify-content-start mt-3">
                                                <Pagination>
                                                    <Pagination.First disabled={current === 0} onClick={() => changeBidPage(0)} />
                                                    <Pagination.Prev  disabled={current === 0} onClick={() => changeBidPage(current - 1)} />
                                                    {Array.from({ length: end - start }).map((_, i) => {
                                                        const p = start + i;
                                                        return <Pagination.Item key={p} active={p === current} onClick={() => changeBidPage(p)}>{p + 1}</Pagination.Item>;
                                                    })}
                                                    <Pagination.Next disabled={current + 1 >= page.totalPages} onClick={() => changeBidPage(current + 1)} />
                                                    <Pagination.Last disabled={current + 1 >= page.totalPages} onClick={() => changeBidPage(page.totalPages - 1)} />
                                                </Pagination>
                                            </Col>
                                        </Row>
                                    );
                                })()}
                            </Tab>

                            {/* Offers */}
                            <Tab eventKey="offer-info" title="Offer">
                                <Card className="shadow-sm">
                                    <Card.Header>
                                        <Row className="mb-2">
                                            <Col><strong>Recent Offers</strong></Col>
                                        </Row>
                                        <Row className="g-2 align-items-center">
                                            <Col md={3}>
                                                <Form.Control size="sm" placeholder="Nickname"
                                                    value={offerSearch.nickname || ""}
                                                    onChange={(e) => {
                                                        const v = e.target.value;
                                                        v ? setOfferSearch({ ...offerSearch, nickname: v })
                                                          : (() => { const { nickname, ...rest } = offerSearch; setOfferSearch(rest); })();
                                                    }} />
                                            </Col>
                                            <Col md={3}>
                                                <Form.Control size="sm" placeholder="Phone"
                                                    value={offerSearch.phoneNo || ""}
                                                    onChange={(e) => {
                                                        const v = e.target.value;
                                                        v ? setOfferSearch({ ...offerSearch, phoneNo: v })
                                                          : (() => { const { phoneNo, ...rest } = offerSearch; setOfferSearch(rest); })();
                                                    }} />
                                            </Col>
                                            <Col md={2}>
                                                <Form.Select size="sm" value={offerSearch.limit || 10}
                                                    onChange={(e) => setOfferSearch(prev => ({
                                                        ...prev,
                                                        limit: Number(e.target.value) === 0 ? undefined : Number(e.target.value)
                                                    }))}>
                                                    <option value={10}>10</option>
                                                    <option value={25}>25</option>
                                                    <option value={0}>All</option>
                                                </Form.Select>
                                            </Col>
                                            <Col md={2} className="d-grid">
                                                <Button size="sm" onClick={() => loadOffers(0)}>🔍 Search</Button>
                                            </Col>
                                        </Row>
                                    </Card.Header>
                                    <Card.Body className="p-0">
                                        <Table hover responsive className="mb-0">
                                            <thead>
                                                <tr>
                                                    <th>Created At</th>
                                                    <th>Account</th>
                                                    <th>Status</th>
                                                    <th>Currency</th>
                                                    <th>Amount</th>
                                                </tr>
                                            </thead>
                                            <tbody>
                                                {(listingDetail.offers?.beans ?? []).length > 0 ? (
                                                    listingDetail.offers!.beans.map((offer, Idx) => (
                                                        <tr key={Idx}>
                                                            <td>{offer.createdAt ? new Date(offer.createdAt).toLocaleString() : "-"}</td>
                                                            <td>{offer.nickname}, {offer.phoneNo}</td>
                                                            <td>
                                                                <Badge bg={
                                                                    offer.status === "ACCEPTED" ? "success"
                                                                    : offer.status === "OPEN" ? "secondary"
                                                                    : "danger"
                                                                }>
                                                                    {offer.status}
                                                                </Badge>
                                                            </td>
                                                            <td>{offer.currency}</td>
                                                            <td>{offer.amount}</td>
                                                        </tr>
                                                    ))
                                                ) : (
                                                    <tr>
                                                        <td colSpan={6} className="text-center text-muted py-3">No records.</td>
                                                    </tr>
                                                )}
                                            </tbody>
                                        </Table>
                                    </Card.Body>
                                </Card>
                                {listingDetail.offers && listingDetail.offers.totalPages > 1 && (() => {
                                    const page = listingDetail.offers;
                                    const current = page.offset ?? 0;
                                    const start = Math.max(current - 2, 0);
                                    const end = Math.min(start + 5, page.totalPages);
                                    return (
                                        <Row>
                                            <Col className="d-flex justify-content-start mt-3">
                                                <Pagination>
                                                    <Pagination.First disabled={current === 0} onClick={() => changeOfferPage(0)} />
                                                    <Pagination.Prev  disabled={current === 0} onClick={() => changeOfferPage(current - 1)} />
                                                    {Array.from({ length: end - start }).map((_, i) => {
                                                        const p = start + i;
                                                        return <Pagination.Item key={p} active={p === current} onClick={() => changeOfferPage(p)}>{p + 1}</Pagination.Item>;
                                                    })}
                                                    <Pagination.Next disabled={current + 1 >= page.totalPages} onClick={() => changeOfferPage(current + 1)} />
                                                    <Pagination.Last disabled={current + 1 >= page.totalPages} onClick={() => changeOfferPage(page.totalPages - 1)} />
                                                </Pagination>
                                            </Col>
                                        </Row>
                                    );
                                })()}
                            </Tab>
                        </Tabs>
                    </Col>
                </Row>
            </Container>

            {/* Media + Attributes (only on Basic Info tab) */}
            {activeKey === "listing-info" && (
                <Container fluid className="mt-4">
                    <Row>
                        <Col md={3}>
                            <div className="mb-2">
                                <h5 style={{ fontWeight: "bold", color: "#4a6ff" }}>
                                    {listingDetail?.medias?.[activeIndex]?.sortOrder || "Media"}
                                </h5>
                            </div>
                            {(listingDetail.medias ?? []).length > 0 ? (
                                <Carousel activeIndex={activeIndex} onSelect={(i) => setActiveIndex(i)}>
                                    {listingDetail.medias!.map((media, idx) => (
                                        <Carousel.Item key={idx}>
                                            <img
                                                src={`${URL_BACKEND_ROOT}/v1/file/api/files/${media.fileUuid}`}
                                                className="d-block w-100"
                                                style={{ maxHeight: 400, objectFit: "contain" }}
                                            />
                                        </Carousel.Item>
                                    ))}
                                </Carousel>
                            ) : (
                                <Card className="p-3 text-center">No media uploaded</Card>
                            )}
                        </Col>
                        <Col md={9}>
                            <Card>
                                <Card.Header>Product Attribute</Card.Header>
                                <Card.Body>
                                    <Table bordered hover>
                                        <thead>
                                            <tr>
                                                <th>Attribute</th>
                                                <th>Status</th>
                                                <th>Choice Value</th>
                                                <th>Verified Status</th>
                                                <th>Value Text</th>
                                                <th>Value Number</th>
                                                <th>Value Bool</th>
                                            </tr>
                                        </thead>
                                        <tbody>
                                            {(listingDetail.attributes ?? []).map((a, idx) => (
                                                <tr key={idx}>
                                                    <td>{a.attribute?.name}</td>
                                                    <td><Badge bg={getAttributeStatus(a.status)}>{a.status}</Badge></td>
                                                    <td>{a.attributeChoice?.valueText}</td>
                                                    <td>{a.verificationStatus}</td>
                                                    <td>{a.valueText}</td>
                                                    <td>{a.valueNumber}</td>
                                                    <td>{a.valueBool ? "true" : "false"}</td>
                                                </tr>
                                            ))}
                                        </tbody>
                                    </Table>
                                </Card.Body>
                            </Card>
                        </Col>
                    </Row>
                </Container>
            )}

            {/* ── Admin modals ── */}
            <ListingSuspendModal
                show={showSuspendModal}
                listingDetail={listingDetail}
                onClose={() => setShowSuspendModal(false)}
                onSuccess={listingDetailGet}
            />
            <ListingCancelModal
                show={showCancelModal}
                listingDetail={listingDetail}
                onClose={() => setShowCancelModal(false)}
                onSuccess={listingDetailGet}
            />
            <ListingRestoreModal
                show={showRestoreModal}
                listingDetail={listingDetail}
                onClose={() => setShowRestoreModal(false)}
                onSuccess={listingDetailGet}
            />
            <ListingPayoutModal
                show={showPayoutModal}
                listingDetail={listingDetail}
                onClose={() => setShowPayoutModal(false)}
                onSuccess={listingDetailGet}
            />
        </div>
    );
};

export default ListingDetail;